import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TimePicker } from "@/components/ui/time-picker";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Moon, CalendarClock, TrendingUp, TrendingDown, Home, Send, AlertOctagon, Globe, Cpu, DollarSign, ZapOff, Trophy, Target, Mail, ExternalLink, Clock, PlayCircle, Scale, FileDown, Sparkles, AlertTriangle } from "lucide-react";
import { formatRelativeShortEs } from "@/lib/relativeTime";

const ALERT_ROWS = [
  { key: "alert_due_dates", type: "due_date", label: "Vencimientos próximos", desc: "Cuotas o pagos a ≤7 días", icon: CalendarClock },
  { key: "alert_overdue", type: "overdue", label: "Vencimientos pasados", desc: "Cuotas vencidas sin marcar pagadas", icon: AlertOctagon },
  { key: "alert_unusual_expenses", type: "unusual_expense", label: "Gastos inusuales", desc: "Movimientos ≥2.5× tu media", icon: TrendingUp },
  { key: "alert_revaluation", type: "revaluation", label: "Revalorización inmobiliaria", desc: "Cambio ≥ umbral entre tasaciones", icon: Home },
  { key: "alert_stale_prices", type: "stale_price", label: "Cotizaciones sin actualizar", desc: "Inversiones sin precio nuevo en >3 días", icon: ZapOff },
  { key: "alert_new_highs", type: "all_time_high", label: "Máximo del año", desc: "Cierre por encima del máximo de 12 meses", icon: Trophy },
  { key: "alert_sharp_drop", type: "sharp_drop", label: "Caída fuerte", desc: "Baja más del 10% en un solo día", icon: TrendingDown },
  { key: "alert_price_target", type: "price_target", label: "Objetivo de precio", desc: "Cuando un activo cruza tu objetivo (↑ o ↓)", icon: Target },
  { key: "alert_high_debt_ratio", type: "high_debt_ratio", label: "Ratio de deuda alto", desc: "Cuando deuda / activos supera tu umbral", icon: Scale },
  { key: "alert_rebalance_drift", type: "rebalance_drift", label: "Desvío de rebalance", desc: "Cuando una clase se desvía más que tu umbral del objetivo", icon: Scale },
  { key: "alert_mortgage_missed_payment", type: "mortgage_missed_payment", label: "Cuota de hipoteca faltante", desc: "Cuando pasada la fecha esperada no detectamos el pago del mes", icon: AlertTriangle },
];

function SchedulerStatus() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);

  const load = () => {
    api.get("/notifications/scheduler-status")
      .then((r) => setStatus(r.data))
      .catch((e) => logger.warn("scheduler status", e));
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/notifications/scheduler-run-now");
      const { processed, sent } = r.data || {};
      toast.success(`Ciclo ejecutado · ${processed} usuarios procesados, ${sent} email${sent === 1 ? "" : "s"} enviado${sent === 1 ? "" : "s"}`);
      load();
    } catch {
      toast.error("No se pudo ejecutar");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      data-testid="prefs-scheduler-status"
      className="pl-7 pt-2 flex flex-col gap-1.5"
    >
      <div className="text-[11px] text-[#5F7368] flex items-center gap-2 flex-wrap">
        <Clock className="w-3 h-3" />
        <span>
          Programador automático:{" "}
          {status?.running ? (
            <span className="text-[#1C7C54] font-semibold">activo</span>
          ) : (
            <span className="text-[#D95D39] font-semibold">inactivo</span>
          )}
        </span>
        {status?.last_cycle_at && (
          <span className="text-[#8A9992]">
            · último ciclo {formatRelativeShortEs(status.last_cycle_at)}
            {" "}({status.last_cycle_sent}/{status.last_cycle_processed})
          </span>
        )}
        <button
          type="button"
          onClick={runNow}
          disabled={running}
          data-testid="prefs-scheduler-run-btn"
          className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-semibold border border-[#EBEBEA] bg-white text-[#0A110E] hover:border-[#1C7C54]/40 disabled:opacity-50"
        >
          <PlayCircle className="w-3 h-3" />
          {running ? "Ejecutando…" : "Ejecutar ahora"}
        </button>
      </div>
      <div className="text-[11px] text-[#8A9992] leading-relaxed">
        Cada 15 min el servidor comprueba qué usuarios cumplen la hora local
        configurada y les envía el resumen. Solo una vez por día.
      </div>
    </div>
  );
}

export default function NotificationsDialog({ open, onOpenChange }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testingType, setTestingType] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [emailSettings, setEmailSettings] = useState(null);
  const [senderInput, setSenderInput] = useState("");
  const [savingSender, setSavingSender] = useState(false);
  const [aiModels, setAiModels] = useState([]);
  const [aiModel, setAiModel] = useState(null);
  const [initialAiModel, setInitialAiModel] = useState(null);
  const [budget, setBudget] = useState("");
  const [initialBudget, setInitialBudget] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/notifications/prefs")
      .then((r) => {
        // Auto-detect tz if empty or UTC and browser has a real tz
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const cur = r.data?.timezone;
        if (tz && (!cur || cur === "UTC")) {
          setPrefs({ ...r.data, timezone: tz });
        } else {
          setPrefs(r.data);
        }
      })
      .catch((e) => logger.error("prefs load", e));
    Promise.all([api.get("/ai/models"), api.get("/ai/prefs")])
      .then(([ms, pref]) => {
        setAiModels(ms.data?.models || []);
        const m = pref.data?.model || ms.data?.default || null;
        setAiModel(m);
        setInitialAiModel(m);
        const b = Number(pref.data?.monthly_budget_usd || 0);
        const bStr = b > 0 ? String(b) : "";
        setBudget(bStr);
        setInitialBudget(bStr);
      })
      .catch((e) => logger.error("ai prefs load", e));
    api.get("/notifications/email-settings")
      .then((r) => {
        setEmailSettings(r.data);
        setSenderInput(r.data?.sender_email || "");
      })
      .catch((e) => logger.warn("email settings load", e));
  }, [open]);

  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));

  const sendTypeTest = async (type) => {
    setTestingType(type);
    try {
      const r = await api.post("/push/test-type", { type });
      if (r.data?.sent > 0) {
        toast.success("Notificación de prueba enviada");
      } else {
        toast.info("Activa las notificaciones para recibir la prueba");
      }
    } catch {
      toast.error("No se pudo enviar la prueba");
    } finally {
      setTestingType(null);
    }
  };

  const sendDigestTest = async () => {
    setSendingDigest(true);
    try {
      const r = await api.post("/notifications/email-digest/test");
      if (r.data?.sent) toast.success(`Email de prueba enviado (${r.data.count} alertas)`);
      else toast.info("No se pudo enviar el email");
    } catch (e) {
      const msg = e.response?.data?.detail || "No se pudo enviar";
      toast.error(msg === "resend_not_configured" || msg.includes("RESEND_API_KEY")
        ? "Falta configurar RESEND_API_KEY en el servidor"
        : msg);
    } finally {
      setSendingDigest(false);
    }
  };

  const saveSender = async () => {
    setSavingSender(true);
    try {
      const r = await api.put("/notifications/email-settings", { sender_email: senderInput || null });
      setEmailSettings(r.data);
      setSenderInput(r.data?.sender_email || "");
      toast.success("Remitente actualizado");
    } catch (e) {
      const msg = e.response?.data?.detail || "No se pudo guardar";
      toast.error(msg);
    } finally {
      setSavingSender(false);
    }
  };

  const [sendingRecap, setSendingRecap] = useState(false);
  const sendRecapTest = async () => {
    setSendingRecap(true);
    try {
      const r = await api.post("/notifications/monthly-recap/test");
      if (r.data?.sent) toast.success("Recap mensual enviado");
      else toast.info("No se pudo enviar el recap");
    } catch (e) {
      const msg = e.response?.data?.detail || "No se pudo enviar";
      toast.error(msg?.startsWith?.("RESEND") || msg?.includes?.("resend")
        ? "Falta configurar RESEND_API_KEY en el servidor"
        : msg);
    } finally {
      setSendingRecap(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const calls = [api.put("/notifications/prefs", prefs)];
      const aiPatch = {};
      if (aiModel && aiModel !== initialAiModel) aiPatch.model = aiModel;
      if (budget !== initialBudget) {
        const parsed = budget === "" ? 0 : Number(budget);
        if (Number.isNaN(parsed) || parsed < 0) {
          toast.error("Presupuesto inválido");
          setSaving(false);
          return;
        }
        aiPatch.monthly_budget_usd = parsed;
      }
      if (Object.keys(aiPatch).length > 0) {
        calls.push(api.put("/ai/prefs", aiPatch));
      }
      await Promise.all(calls);
      toast.success("Preferencias guardadas");
      onOpenChange(false);
    } catch {
      toast.error("No se pudieron guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Preferencias</DialogTitle>
        </DialogHeader>

        {!prefs ? (
          <div className="p-4 text-sm text-[#5F7368]">Cargando…</div>
        ) : (
          <div className="flex flex-col gap-5 py-2">
            {/* AI Model preference */}
            <div className="flex flex-col gap-2">
              <div className="text-xs uppercase tracking-[0.1em] font-bold text-[#8A9992]">
                Modelo IA
              </div>
              <div className="flex items-start gap-3">
                <Cpu className="w-4 h-4 mt-0.5 text-[#1C7C54]" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0A110E]">Motor por defecto</div>
                  <div className="text-xs text-[#8A9992]">
                    Se usa en el chat, el análisis de extractos y los insights.
                    Puedes cambiarlo por conversación en el asistente.
                  </div>
                </div>
                <select
                  data-testid="prefs-ai-model"
                  value={aiModel || ""}
                  onChange={(e) => setAiModel(e.target.value)}
                  disabled={aiModels.length === 0}
                  className="w-44 h-9 px-2 rounded-md border border-[#EBEBEA] bg-white text-xs text-[#0A110E] focus:outline-none focus:border-[#1C7C54]/60 disabled:opacity-60"
                >
                  {aiModels.length === 0 && <option value="">Cargando…</option>}
                  {aiModels.map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-start gap-3">
                <DollarSign className="w-4 h-4 mt-0.5 text-[#1C7C54]" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0A110E]">Presupuesto mensual (USD)</div>
                  <div className="text-xs text-[#8A9992]">
                    Recibirás un aviso cuando el gasto estimado supere el 80%. Deja en blanco o 0 para desactivar.
                  </div>
                </div>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  inputMode="decimal"
                  data-testid="prefs-ai-budget"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  className="w-24 text-right"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Alert types */}
            <div className="flex flex-col gap-3 pt-4 border-t border-[#EBEBEA]">
              <div className="text-xs uppercase tracking-[0.1em] font-bold text-[#8A9992]">
                Tipos de alerta
              </div>
              {ALERT_ROWS.map(({ key, type, label, desc, icon: Icon }) => (
                <div key={key} className="flex items-start gap-3">
                  <Icon className="w-4 h-4 mt-0.5 text-[#1C7C54]" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#0A110E]">{label}</div>
                    <div className="text-xs text-[#8A9992]">{desc}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => sendTypeTest(type)}
                    disabled={testingType === type}
                    data-testid={`prefs-test-${type}`}
                    title="Enviar prueba de este tipo"
                    className="p-1.5 rounded-lg hover:bg-[#EBEBEA] text-[#5F7368] disabled:opacity-50"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                  <Switch
                    data-testid={`prefs-${key}`}
                    checked={!!prefs[key]}
                    onCheckedChange={(v) => set(key, v)}
                  />
                </div>
              ))}
              {/* Custom threshold input for the debt ratio alert */}
              {prefs.alert_high_debt_ratio && (
                <div className="flex items-center gap-3 pl-7 -mt-1">
                  <Label className="text-xs">Umbral deuda/activos</Label>
                  <Input
                    type="number"
                    min="1" max="100" step="1"
                    data-testid="prefs-debt-threshold"
                    value={prefs.debt_ratio_threshold ?? 60}
                    onChange={(e) => set("debt_ratio_threshold", Number(e.target.value))}
                    className="h-8 w-24"
                  />
                  <span className="text-[11px] text-[#8A9992]">%</span>
                </div>
              )}
              {/* Custom threshold input for the debt ratio CHANGE alert */}
              {prefs.alert_debt_ratio_change && (
                <div className="flex items-center gap-3 pl-7 -mt-1">
                  <Label className="text-xs">Umbral de cambio mensual</Label>
                  <Input
                    type="number"
                    min="1" max="50" step="0.5"
                    data-testid="prefs-debt-change-threshold"
                    value={prefs.debt_ratio_change_threshold ?? 5}
                    onChange={(e) => set("debt_ratio_change_threshold", Number(e.target.value))}
                    className="h-8 w-24"
                  />
                  <span className="text-[11px] text-[#8A9992]">pts vs mes anterior</span>
                </div>
              )}
              {/* Custom threshold input for the rebalance-drift alert */}
              {prefs.alert_rebalance_drift && (
                <div className="flex items-center gap-3 pl-7 -mt-1">
                  <Label className="text-xs">Umbral de desvío</Label>
                  <Input
                    type="number"
                    min="1" max="50" step="0.5"
                    data-testid="prefs-drift-threshold"
                    value={prefs.drift_alert_threshold_pct ?? 10}
                    onChange={(e) => set("drift_alert_threshold_pct", Number(e.target.value))}
                    className="h-8 w-24"
                  />
                  <span className="text-[11px] text-[#8A9992]">pts sobre el objetivo</span>
                </div>
              )}
              {/* Grace-days input for the missing-mortgage-payment alert */}
              {prefs.alert_mortgage_missed_payment && (
                <div className="flex items-center gap-3 pl-7 -mt-1">
                  <Label className="text-xs">Días de gracia</Label>
                  <Input
                    type="number"
                    min="0" max="30" step="1"
                    data-testid="prefs-missed-grace-days"
                    value={prefs.mortgage_missed_grace_days ?? 5}
                    onChange={(e) => set("mortgage_missed_grace_days", Number(e.target.value))}
                    className="h-8 w-24"
                  />
                  <span className="text-[11px] text-[#8A9992]">tras la fecha esperada</span>
                </div>
              )}
              {/* Add a special row for the debt-change alert type inside ALERT_ROWS via… */}
              <div className="flex items-start gap-3">
                <Scale className="w-4 h-4 mt-0.5 text-[#1C7C54]" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0A110E]">Cambio en ratio de deuda</div>
                  <div className="text-xs text-[#8A9992]">Notifícame si mi ratio sube o baja X puntos vs mes anterior</div>
                </div>
                <button
                  type="button"
                  onClick={() => sendTypeTest("debt_ratio_change")}
                  disabled={testingType === "debt_ratio_change"}
                  data-testid="prefs-test-debt_ratio_change"
                  title="Enviar prueba"
                  className="p-1.5 rounded-lg hover:bg-[#EBEBEA] text-[#5F7368] disabled:opacity-50"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
                <Switch
                  data-testid="prefs-alert_debt_ratio_change"
                  checked={!!prefs.alert_debt_ratio_change}
                  onCheckedChange={(v) => set("alert_debt_ratio_change", v)}
                />
              </div>
              {/* AI auto-link threshold */}
              <div className="flex items-start gap-3 pt-2 border-t border-[#EBEBEA] mt-2">
                <Sparkles className="w-4 h-4 mt-0.5 text-[#1C7C54]" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0A110E]">Auto-aceptar sugerencias IA</div>
                  <div className="text-xs text-[#8A9992]">
                    Cuando la IA sugiera un inmueble para un gasto con confianza ≥ este umbral, la app lo vincula sola.
                  </div>
                </div>
                <Input
                  type="number"
                  min="0" max="100" step="5"
                  data-testid="prefs-ai-autolink-threshold"
                  value={Math.round((prefs.ai_autolink_threshold ?? 0.9) * 100)}
                  onChange={(e) => {
                    const raw = Math.min(100, Math.max(0, Number(e.target.value)));
                    set("ai_autolink_threshold", raw / 100);
                  }}
                  className="h-8 w-20"
                />
                <span className="text-[11px] text-[#8A9992]">%</span>
              </div>
            </div>

            {/* Email digest */}
            <div className="flex flex-col gap-3 pt-4 border-t border-[#EBEBEA]">
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-[#1C7C54]" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0A110E]">Email diario de resumen</div>
                  <div className="text-xs text-[#8A9992]">
                    Recibirás un email con las alertas críticas del día (caídas fuertes, máximos, vencimientos, objetivos).
                  </div>
                </div>
                <button
                  type="button"
                  onClick={sendDigestTest}
                  disabled={sendingDigest}
                  data-testid="prefs-test-email-digest"
                  title="Enviar email de prueba"
                  className="p-1.5 rounded-lg hover:bg-[#EBEBEA] text-[#5F7368] disabled:opacity-50"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
                <Switch
                  data-testid="prefs-alert_email_digest"
                  checked={!!prefs.alert_email_digest}
                  onCheckedChange={(v) => set("alert_email_digest", v)}
                />
              </div>
              {prefs.alert_email_digest && (
                <>
                  <div className="flex items-center gap-3 pl-7">
                    <Label className="text-xs">Hora de envío</Label>
                    <select
                      data-testid="prefs-email-digest-hour"
                      value={prefs.email_digest_hour ?? 8}
                      onChange={(e) => set("email_digest_hour", Number(e.target.value))}
                      className="h-8 px-2 rounded-md border border-[#EBEBEA] bg-white text-xs text-[#0A110E] focus:outline-none focus:border-[#1C7C54]/60"
                    >
                      {Array.from({ length: 24 }).map((_, h) => (
                        <option key={`hour-${h}`} value={h}>{String(h).padStart(2, "0")}:00</option>
                      ))}
                    </select>
                    <span className="text-[11px] text-[#8A9992]">en tu zona horaria configurada</span>
                  </div>

                  {/* Sender email + domain guidance */}
                  <div className="pl-7 pt-1">
                    <Label className="text-xs">Remitente del email</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="email"
                        data-testid="prefs-sender-email"
                        value={senderInput}
                        onChange={(e) => setSenderInput(e.target.value)}
                        placeholder="alertas@tudominio.com"
                        className="flex-1 h-9"
                      />
                      <Button
                        onClick={saveSender}
                        disabled={savingSender || senderInput === (emailSettings?.sender_email || "")}
                        data-testid="prefs-sender-save-btn"
                        className="h-9 bg-[#0F291E] hover:bg-[#1C7C54]"
                      >
                        {savingSender ? "…" : "Guardar"}
                      </Button>
                    </div>
                    {emailSettings?.is_sandbox ? (
                      <div
                        data-testid="prefs-sandbox-warning"
                        className="mt-2 text-[11px] text-[#8B6B1F] bg-[#C89A3F]/10 border border-[#C89A3F]/30 rounded-md p-2 leading-relaxed"
                      >
                        Estás usando el remitente sandbox de Resend
                        (<code className="font-mono">onboarding@resend.dev</code>).
                        Solo puedes recibir emails en la dirección de tu cuenta Resend.{" "}
                        <a
                          href="https://resend.com/domains"
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 underline hover:text-[#0A110E]"
                        >
                          Verifica tu dominio <ExternalLink className="w-3 h-3" />
                        </a>{" "}
                        y cambia el remitente aquí para enviar desde tu marca.
                      </div>
                    ) : (
                      <div className="mt-2 text-[11px] text-[#1C7C54] leading-relaxed">
                        ✓ Remitente personalizado configurado. Los emails se envían desde este dominio.
                      </div>
                    )}
                  </div>

                  {/* Scheduler status */}
                  <SchedulerStatus />
                </>
              )}
            </div>

            {/* Monthly recap email */}
            <div className="flex flex-col gap-2 pt-4 border-t border-[#EBEBEA]">
              <div className="flex items-start gap-3">
                <FileDown className="w-4 h-4 mt-0.5 text-[#1C7C54]" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0A110E]">Recap mensual por email</div>
                  <div className="text-xs text-[#8A9992]">
                    El día 1 de cada mes te enviamos el PDF del mes anterior con patrimonio,
                    gastos por categoría y evolución.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={sendRecapTest}
                  disabled={sendingRecap}
                  data-testid="prefs-test-monthly-recap"
                  title="Enviar recap de prueba"
                  className="p-1.5 rounded-lg hover:bg-[#EBEBEA] text-[#5F7368] disabled:opacity-50"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
                <Switch
                  data-testid="prefs-alert_monthly_recap_email"
                  checked={!!prefs.alert_monthly_recap_email}
                  onCheckedChange={(v) => set("alert_monthly_recap_email", v)}
                />
              </div>
            </div>

            {/* Timezone */}
            <div className="flex items-start gap-3 pt-4 border-t border-[#EBEBEA]">
              <Globe className="w-4 h-4 mt-0.5 text-[#0F291E]" />
              <div className="flex-1">
                <div className="text-sm font-medium text-[#0A110E]">Zona horaria</div>
                <div className="text-xs text-[#8A9992]">
                  El horario silencioso se interpreta en esta zona.
                </div>
              </div>
              <Input
                type="text"
                data-testid="prefs-timezone"
                value={prefs.timezone || "UTC"}
                onChange={(e) => set("timezone", e.target.value)}
                className="w-40"
                placeholder="Europe/Madrid"
              />
            </div>

            {/* Quiet hours */}
            <div className="flex flex-col gap-3 pt-4 border-t border-[#EBEBEA]">
              <div className="flex items-start gap-3">
                <Moon className="w-4 h-4 mt-0.5 text-[#0F291E]" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0A110E]">Horario silencioso</div>
                  <div className="text-xs text-[#8A9992]">
                    Durante este rango no llegarán notificaciones. Se usa tu zona horaria configurada.
                  </div>
                </div>
                <Switch
                  data-testid="prefs-quiet-enabled"
                  checked={!!prefs.quiet_enabled}
                  onCheckedChange={(v) => set("quiet_enabled", v)}
                />
              </div>
              {prefs.quiet_enabled && (
                <div className="grid grid-cols-2 gap-3 pl-7">
                  <div>
                    <Label className="text-xs">Empieza</Label>
                    <div className="mt-1">
                      <TimePicker
                        testid="prefs-quiet-start"
                        value={prefs.quiet_start || "22:00"}
                        onChange={(v) => set("quiet_start", v)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Termina</Label>
                    <div className="mt-1">
                      <TimePicker
                        testid="prefs-quiet-end"
                        value={prefs.quiet_end || "08:00"}
                        onChange={(v) => set("quiet_end", v)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            data-testid="prefs-save-btn"
            onClick={save}
            disabled={!prefs || saving}
            className="bg-[#0F291E] hover:bg-[#1C7C54]"
          >
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
